import { BatchInvoiceUploadCategory } from '../../api/gql';
import { XmlCustomer, XmlCustomerData } from './customer';
import { XmlInvoice } from './invoice';
import { XmlVendor, XmlVendorData } from './vendor';

export class XmlData {
  #customers: XmlCustomer[] = [];
  #customersForUpsert: XmlCustomer[] = [];
  #vendors: XmlVendor[] = [];
  #vendorsForUpsert: XmlVendor[] = [];
  #xmlInvoices: XmlInvoice[] = [];
  #batchId = '';
  #scenarioId = '';
  #contactsConfirmed = false;

  constructor(
    xmlCustomers?: XmlCustomer[],
    xmlInvoices?: XmlInvoice[],
    xmlVendors?: XmlVendor[],
    batchId?: string,
    contactsConfirmed?: boolean,
    scenarioId?: string,
  ) {
    this.#customers = xmlCustomers ?? []; //this is just for sake of correct summary data
    this.#vendors = xmlVendors ?? []; //this is just for sake of correct summary data
    this.#customersForUpsert = xmlCustomers?.filter((c) => c.shouldBeUpserted) ?? []; // contact that is relevant in xmlData context
    this.#vendorsForUpsert = xmlVendors?.filter((v) => v.shouldBeUpserted) ?? []; // contact that is relevant in xmlData context
    this.#xmlInvoices = xmlInvoices ?? [];
    this.#batchId = batchId ?? '';
    this.#contactsConfirmed = !!contactsConfirmed;
    this.#scenarioId = scenarioId ?? '';
  }
  get xmlCustomers() {
    return this.#customersForUpsert;
  }
  get xmlVendors() {
    return this.#vendorsForUpsert;
  }
  get xmlCustomersWithoutContactInfo() {
    return this.#customersForUpsert.filter((c) => !c.hasContactInfo());
  }
  get xmlInvoices() {
    return this.#xmlInvoices;
  }
  get noInvoices() {
    return this.#xmlInvoices.length;
  }
  get noCustomers() {
    return this.#customers.length;
  }
  get noCustomersForUpsertWithoutContactInfo() {
    return this.#customersForUpsert.filter((c) => !c.hasContactInfo()).length;
  }
  get invoiceCategoriesSummary() {
    const categories = this.#xmlInvoices.map((i) => i.category);
    const categoriesSummary = categories.reduce(
      (acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      },
      {} as Record<BatchInvoiceUploadCategory, number>,
    );
    return categoriesSummary;
  }
  get isEmpty() {
    return this.noInvoices === 0 && this.noCustomers === 0;
  }
  get batchId() {
    return this.#batchId;
  }
  set batchId(batchId) {
    this.#batchId = batchId;
  }
  get contactsConfirmed(): boolean {
    return this.#contactsConfirmed;
  }

  set contactsConfirmed(value: boolean) {
    this.#contactsConfirmed = value;
  }
  get scenarioId(): string {
    return this.#scenarioId;
  }

  set scenarioId(value: string) {
    this.#scenarioId = value;
  }
  addUniqueCustomer(customer: XmlCustomer) {
    if (
      this.#customers.some(
        (c) => c.id === customer.id || (c.details.vatId && c.details.vatId === customer.details.vatId),
      )
    ) {
      return;
    }
    this.#customers.push(customer);
  }
  addUniqueCustomerForUpsert(customer: XmlCustomer) {
    const customerWithSameId = this.#customersForUpsert.find(
      (c) => c.id === customer.id || (c.details.vatId && c.details.vatId === customer.details.vatId),
    );
    if (customerWithSameId) {
      // customer with same id or vatId already exists, copy its invoices IDs.
      customerWithSameId.addInvoiceIds(customer.invoices);
      return;
    }
    this.#customersForUpsert.push(customer);
  }

  addUniqueVendor(vendor: XmlVendor) {
    if (
      this.#vendors.some((v) => v.id === vendor.id || (v.details.vatId && v.details.vatId === vendor.details.vatId))
    ) {
      return;
    }
    this.#vendors.push(vendor);
  }
  addUniqueVendorForUpsert(vendor: XmlVendor) {
    const vendorWithSameId = this.#vendorsForUpsert.find(
      (v) => v.id === vendor.id || (v.details.vatId && v.details.vatId === vendor.details.vatId),
    );
    if (vendorWithSameId) {
      // vendor with same id or vatId already exists, copy its invoices IDs.
      vendorWithSameId.addInvoiceIds(vendor.invoices);
      return;
    }

    this.#vendorsForUpsert.push(vendor);
  }
  addInvoice(invoice: XmlInvoice) {
    this.#xmlInvoices.push(invoice);
  }
  getCustomer(id: string) {
    return this.#customersForUpsert.find((c) => c.id === id);
  }
  getVendor(id: string) {
    return this.#vendorsForUpsert.find((v) => v.id === id);
  }
  getCustomerInvoices(customer: XmlCustomer) {
    const invoiceIds = customer.invoices;
    return this.#xmlInvoices.filter((i) => invoiceIds.includes(i.id));
  }
  hasCustomerWithoutContactInfo() {
    return this.#customersForUpsert.some((c) => c.shouldBeUpserted && !c.hasContactInfo());
  }
  hasContactToUpsert() {
    return this.#customersForUpsert.some((c) => !c.isUpserted) || this.#vendorsForUpsert.some((v) => !v.isUpserted);
  }
  updateCustomer(id: string, payload: XmlCustomerData) {
    const customer = this.getCustomer(id);
    if (!customer) {
      return;
    }
    customer.details = { ...customer.details, ...payload };
  }

  updateVendor(id: string, payload: XmlVendorData) {
    const vendor = this.getVendor(id);
    if (!vendor) {
      return;
    }
    vendor.details = { ...vendor.details, ...payload };
  }

  setContactUpserted(id: string, contactUpserted = true) {
    const contact = this.getCustomer(id) ?? this.getVendor(id);

    if (!contact) {
      console.error('Contact with id ', id, 'was not found.');
      return;
    }
    contact.isUpserted = contactUpserted;
  }

  get xmlInvoicesForUpload() {
    return this.#xmlInvoices.filter((i) => i.isForUpload);
  }

  get hasInvoicesForBilling() {
    return this.#xmlInvoices.some((i) => i.category === BatchInvoiceUploadCategory.New);
  }
}
